import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setCredentials, clearCredentials } from "./authSlice";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://stagingapi.acadimyst.com/";

// Primary base query — reads token from Redux state, always sends cookies
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Dedicated refresh base query — NO Authorization header, sends cookies only
const baseQueryForRefresh = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: "include",
});

// Module-level promise used as a mutex to deduplicate concurrent refresh calls
let refreshPromise = null;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQueryWithAuth(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // If the session hard-expiry time has passed, clear immediately — no refresh
    const maxSessionUntil = api.getState().auth.maxSessionUntil;
    if (maxSessionUntil && new Date() > new Date(maxSessionUntil)) {
      api.dispatch(clearCredentials());
      return result;
    }

    // Deduplicate: all concurrent 401s share the same refresh promise
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          return await baseQueryForRefresh(
            {
              url: "api/v1/en/student/auth/refresh-token",
              method: "POST",
              body: {},
            },
            api,
            extraOptions,
          );
        } finally {
          // Nulled before awaiters resume so the next batch creates a fresh promise
          refreshPromise = null;
        }
      })();
    }

    const refreshResult = await refreshPromise;

    if (refreshResult?.data) {
      const newToken = refreshResult.data?.data?.data?.token;
      const newMaxSession = refreshResult.data?.data?.data?.maxSessionUntil;
      if (newToken) {
        api.dispatch(
          setCredentials({ token: newToken, maxSessionUntil: newMaxSession }),
        );
        // Retry the original request with the new token
        result = await baseQueryWithAuth(args, api, extraOptions);
      } else {
        api.dispatch(clearCredentials());
      }
    } else {
      api.dispatch(clearCredentials());
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ["Login", "ListeningTests"],
  endpoints: (builder) => ({}), // add your endpoints here
});
